import type { SetFolderInputs } from '@st/sdk'

export const MAX_FINALIZED_QUEUE_ITEMS = 5

type RequestWithType = { type: string }

export type RequestQueue<Request extends RequestWithType> = {
  items: Array<RequestQueueItem<Request>>
}

type RequestQueueOp<Request extends RequestWithType> = {
  request: Request
}

type QueuedRequestQueueItem<Request extends RequestWithType> = {
  id: string
  status: 'queued'
  op: RequestQueueOp<Request>
}

type RunningRequestQueueItem<Request extends RequestWithType> = {
  id: string
  status: 'running'
  op: RequestQueueOp<Request>
}

type SettledRequestQueueItem<Request extends RequestWithType> = {
  id: string
  status: 'settled'
  settledAtVsn: number
  op: RequestQueueOp<Request>
}

type FinalizedRequestQueueItem<Request extends RequestWithType> = {
  id: string
  status: 'finalized'
  op: RequestQueueOp<Request>
}

export type RequestQueueItem<Request extends RequestWithType> =
  | QueuedRequestQueueItem<Request>
  | RunningRequestQueueItem<Request>
  | SettledRequestQueueItem<Request>
  | FinalizedRequestQueueItem<Request>

/**
 * Request lifecycle:
 * - queued: user intent exists, but the scheduler has not started the RPC yet
 * - running: the scheduler emitted requestStarted and the RPC is in flight
 * - settled: the RPC returned, but we still keep optimistic state alive until
 *   canonical folder state advances past settledAtVsn
 * - finalized: canonical folder state advanced, so the optimistic queue item is
 *   no longer needed
 *
 * "settled" is not a loading state. Domain follow-up messages should already be
 * available once a request reaches settled.
 */

type SetFolderInputsRequest<Request extends RequestWithType> = Extract<Request, SetFolderInputs>

type InputQueueMergeCandidate<Request extends RequestWithType> = QueuedRequestQueueItem<
  SetFolderInputsRequest<Request>
>

function isInputQueueMergeCandidate<Request extends RequestWithType>(
  item: RequestQueueItem<Request>
): item is InputQueueMergeCandidate<Request> {
  return item.status == 'queued' && item.op.request.type == 'folders/setFolderInputs'
}

function addToRequestQueue<Request extends RequestWithType>(
  requestQueue: RequestQueue<Request>,
  item: RequestQueueItem<Request>
): RequestQueue<Request> {
  return { items: [...requestQueue.items, item] }
}

export function emptyRequestQueue<Request extends RequestWithType>(): RequestQueue<Request> {
  return { items: [] }
}

export function markRequestStarted<Request extends RequestWithType>(
  requestQueue: RequestQueue<Request>,
  requestId: string
): RequestQueue<Request> {
  let changed = false
  const updatedItems: RequestQueueItem<Request>[] = requestQueue.items.map((item) => {
    if (item.id == requestId && item.status == 'queued') {
      changed = true
      return {
        id: item.id,
        status: 'running',
        op: item.op
      } satisfies RunningRequestQueueItem<Request>
    }
    return item
  })

  if (!changed) {
    return requestQueue
  }
  return { ...requestQueue, items: updatedItems }
}

export function markRequestSettledAtVsn<Request extends RequestWithType>(
  requestQueue: RequestQueue<Request>,
  requestId: string,
  currentFolderStateVsn: number
): RequestQueue<Request> {
  let changed = false
  const updatedItems: RequestQueueItem<Request>[] = requestQueue.items.map((item) => {
    if (item.id == requestId && item.status == 'running') {
      changed = true
      return {
        status: 'settled',
        id: requestId,
        settledAtVsn: currentFolderStateVsn,
        op: item.op
      } satisfies SettledRequestQueueItem<Request>
    }
    return item
  })

  if (!changed) {
    return requestQueue
  }
  return { ...requestQueue, items: updatedItems }
}

export function markRequestFailed<Request extends RequestWithType>(
  requestQueue: RequestQueue<Request>,
  requestId: string
): RequestQueue<Request> {
  const runningItem = requestQueue.items.find(
    (item) => item.id == requestId && item.status == 'running'
  )
  if (!runningItem) {
    return requestQueue
  }
  const nextQueue = {
    ...requestQueue,
    items: requestQueue.items.filter((item) => item.id != requestId)
  }
  return nextQueue
}

export function finalizeQueueItem<Request extends RequestWithType>(
  requestQueue: RequestQueue<Request>,
  itemId: string
): RequestQueue<Request> {
  return finalizeSettledQueueItems(requestQueue, (item) => item.id == itemId)
}

export function finalizeSettledQueueByVsn<Request extends RequestWithType>(
  requestQueue: RequestQueue<Request>,
  currentFolderStateVsn: number
): RequestQueue<Request> {
  return finalizeSettledQueueItems(
    requestQueue,
    (item) => currentFolderStateVsn > item.settledAtVsn
  )
}

function finalizeSettledQueueItems<Request extends RequestWithType>(
  requestQueue: RequestQueue<Request>,
  shouldFinalize: (item: SettledRequestQueueItem<Request>) => boolean
): RequestQueue<Request> {
  let changed = false
  const updatedItems = requestQueue.items.map((item) => {
    if (item.status != 'settled') return item
    if (!shouldFinalize(item)) return item
    changed = true
    return {
      status: 'finalized',
      id: item.id,
      op: item.op
    } satisfies FinalizedRequestQueueItem<Request>
  })

  if (!changed) {
    return requestQueue
  }

  const nextItems = pruneFinalizedQueueItems(updatedItems)
  return { ...requestQueue, items: nextItems }
}

function pruneFinalizedQueueItems<Request extends RequestWithType>(
  items: RequestQueueItem<Request>[]
): RequestQueueItem<Request>[] {
  const finalizedCount = items.filter((item) => item.status == 'finalized').length
  let toRemove = Math.max(0, finalizedCount - MAX_FINALIZED_QUEUE_ITEMS)
  const finalItems: RequestQueueItem<Request>[] = []
  for (const item of items) {
    if (item.status == 'finalized' && toRemove > 0) {
      toRemove--
      continue
    }
    finalItems.push(item)
  }
  return finalItems
}

function mergeRequests(requests: SetFolderInputs[]): SetFolderInputs['inputs'] {
  const request: SetFolderInputs = {
    type: 'folders/setFolderInputs',
    folderId: requests[0].folderId,
    inputs: {}
  }
  for (const entry of requests) {
    Object.assign(request.inputs, entry.inputs)
  }
  return request.inputs
}

function mergeInputRequests<Request extends RequestWithType>(
  requestQueue: RequestQueue<Request>
): RequestQueue<Request> {
  const mergeableItems = requestQueue.items.filter(
    (item): item is InputQueueMergeCandidate<Request> => {
      return item.status == 'queued' && item.op.request.type == 'folders/setFolderInputs'
    }
  )
  if (mergeableItems.length == 0) {
    return requestQueue
  }

  const latestMergeItem = mergeableItems[mergeableItems.length - 1]
  const mergedItem = {
    status: 'queued',
    id: latestMergeItem.id,
    op: {
      request: {
        ...latestMergeItem.op.request,
        inputs: mergeRequests(mergeableItems.map((item) => item.op.request))
      }
    }
  } satisfies QueuedRequestQueueItem<SetFolderInputsRequest<Request>>

  let replaced = false
  const finalItems: RequestQueueItem<Request>[] = []
  for (const item of requestQueue.items) {
    if (!isInputQueueMergeCandidate(item)) {
      finalItems.push(item)
      continue
    }

    if (!replaced && item.id == mergedItem.id) {
      finalItems.push(mergedItem)
      replaced = true
    }
  }
  if (!replaced) {
    finalItems.push(mergedItem)
  }

  return { ...requestQueue, items: finalItems }
}

/**
 * The owning module supplies an ID that is unique for its entire lifetime. The queue is reset
 * with a folder snapshot, so it cannot safely own command identity itself.
 */
export function enqueueRequest<Request extends RequestWithType>(
  requestQueue: RequestQueue<Request>,
  requestId: string,
  request: Request
): RequestQueue<Request> {
  const queueItem: QueuedRequestQueueItem<Request> = {
    status: 'queued',
    id: requestId,
    op: { request }
  }
  const newQueue = addToRequestQueue(requestQueue, queueItem)

  if (request.type != 'folders/setFolderInputs') {
    return newQueue
  }

  return mergeInputRequests(newQueue)
}

export function selectQueuedRequestById<
  Request extends RequestWithType,
  RequestType extends Request['type']
>(
  requestQueue: RequestQueue<Request>,
  args: { requestId: string; type: RequestType }
): Extract<Request, { type: RequestType }> | null {
  const item = requestQueue.items.find((entry) => entry.id == args.requestId)
  if (!item || item.status != 'queued') {
    return null
  }
  if (item.op.request.type != args.type) {
    return null
  }
  return item.op.request as Extract<Request, { type: RequestType }>
}
